import { ResponseResult } from '~/core/api/ResponseTypes';
import { QuerySetConfig } from '~/core/configuration/configuration-set.type';
import { Configuration } from '~/core/configuration/configuration.type';
import { QuerySetResults_State } from '../types/state-results.schema';
import { FindQuerySetDataFunction, FindQuerySetDataParams, FindQuerySetDataResult } from './type/findQuerySetData.type';

/**
 * Given the QuerySet ID, state and config, it returns all data relevant to the QuerySet (i.e. its initial configuration, its current state and its controls state)
 */
export function findQuerySetData<
    R extends ResponseResult | QuerySetResults_State | undefined,
    C extends Configuration | undefined = undefined
>(): FindQuerySetDataFunction<R, C> {
    const innerFunction = (
        querySetIdentifier: string,
        { state, config, results }: FindQuerySetDataParams<R, C>
    ): FindQuerySetDataResult<R, C> => {
        const querySetState = state.sets.find((set) => set.querySet.identifier === querySetIdentifier);
        if (!querySetState) {
            throw new Error(`Set state not found for ${querySetIdentifier}`);
        }

        const querySetControls = state.controls.find((set) => set.querySet.identifier === querySetIdentifier);
        if (!querySetControls) {
            throw new Error(`Set controls not found for ${querySetIdentifier}`);
        }

        const querySetConfig = (() => {
            if (!config) {
                return undefined;
            }
            const querySetConfig = config.layout.search.find(
                (setConfig) => setConfig.querySet.identifier === querySetIdentifier
            );
            if (!querySetConfig) {
                throw new Error(`Set config not found for ${querySetIdentifier}`);
            }
            return querySetConfig;
        })() as C extends undefined ? undefined : QuerySetConfig;

        const querySetResults = (() => {
            if (!results) {
                return undefined;
            }

            const typedResults: (ResponseResult | QuerySetResults_State)[] | undefined = results;

            if (typedResults === undefined) {
                return undefined;
            }

            const querySetResults = typedResults.find(
                (resultsSet) => resultsSet.querySetIdentifier === querySetIdentifier
            );
            if (!querySetResults) {
                throw new Error(`Set results not found for ${querySetIdentifier}`);
            }
            return querySetResults;
        })() as R extends undefined ? undefined : R;

        return {
            querySetState,
            querySetControls,
            querySetConfig,
            querySetResults,
        };
    };

    return innerFunction;
}
